import { Directive, ElementRef, Input, AfterViewInit, OnChanges, OnDestroy, Optional, SimpleChange, SimpleChanges } from '@angular/core';
import { NgControl } from '@angular/forms';
import { parseEditMask, hasCaseConversion, ParsedEditMask } from '@Core/utils/edit-mask-utils';
import { MaskedTextBoxComponent } from '@progress/kendo-angular-inputs';

/**
 * Directive that extends a kendo-maskedtextbox with numbering mask support,
 * including case-conversion (>, <), termination (|), and escape (\) characters.
 *
 * Usage:
 *   <kendo-maskedtextbox [appNumberingMask]="editMask" ...></kendo-maskedtextbox>
 *
 * The directive:
 *   1. Parses the special chars (>, <, |, \) from the input mask
 *   2. Sets the cleaned mask on the Kendo component
 *   3. Listens for native input events and applies case conversion
 *   4. Preserves cursor position during transformation
 *   5. Updates the NgControl value and validity
 */
@Directive({
    selector: 'kendo-maskedtextbox[appNumberingMask]',
    standalone: false,
})
export class NumberingMaskDirective implements OnChanges, AfterViewInit, OnDestroy {
    @Input('appNumberingMask') rawMask = '';

    private parsedMask!: ParsedEditMask;
    private inputEl: HTMLInputElement | null = null;
    private boundOnInput = this.onInput.bind(this);
    private listenerAttached = false;

    constructor(
        private el: ElementRef,
        @Optional() private ngControl: NgControl,
        @Optional() private maskedTextBox: MaskedTextBoxComponent,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['rawMask']) {
            this.parsedMask = parseEditMask(this.rawMask);
            this.applyMaskToKendo();
            this.updateListener();
        }
    }

    ngAfterViewInit(): void {
        // The native <input> is only available after the view initializes
        this.inputEl = this.el.nativeElement.querySelector('input');
        this.updateListener();
    }

    ngOnDestroy(): void {
        this.removeListener();
    }

    /**
     * Applies the parsed Kendo-safe mask to the MaskedTextBoxComponent.
     *
     * Kendo rebuilds its internal mask tokens inside ngOnChanges when it detects
     * the 'mask' input changed. Since we set the mask programmatically (not via
     * template binding), we use Angular's SimpleChange to notify Kendo properly.
     * If Kendo's internal API changes, only this method needs updating.
     */
    private applyMaskToKendo(): void {
        if (!this.maskedTextBox) return;
        const prev = this.maskedTextBox.mask;
        this.maskedTextBox.mask = this.parsedMask.kendoMask;
        this.maskedTextBox.ngOnChanges({
            mask: new SimpleChange(prev, this.parsedMask.kendoMask, !prev)
        });
    }

    private updateListener(): void {
        this.removeListener();
        if (this.inputEl && this.parsedMask && hasCaseConversion(this.parsedMask)) {
            this.inputEl.addEventListener('input', this.boundOnInput);
            this.listenerAttached = true;
        }
    }

    private removeListener(): void {
        if (this.inputEl && this.listenerAttached) {
            this.inputEl.removeEventListener('input', this.boundOnInput);
            this.listenerAttached = false;
        }
    }

    private onInput(): void {
        if (!this.inputEl) return;
        const value = this.inputEl.value;
        if (!value) return;
        const transformed = this.parsedMask.transformValue(value);
        if (transformed !== value) {
            const cursorPos = this.inputEl.selectionStart;
            this.inputEl.value = transformed;
            this.inputEl.setSelectionRange(cursorPos, cursorPos);
            if (this.ngControl?.control) {
                // emitModelToViewChange: false prevents Angular from calling Kendo's
                // writeValue(), which would overwrite the DOM and reset the cursor.
                this.ngControl.control.setValue(transformed, { emitEvent: false, emitModelToViewChange: false });
                this.ngControl.control.updateValueAndValidity({ emitEvent: false });
            }
            // Kendo/Angular change detection runs after this handler and may reset
            // the cursor position. Re-apply in the next macrotask to guarantee it sticks.
            setTimeout(() => {
                this.inputEl?.setSelectionRange(cursorPos, cursorPos);
            });
        }
    }
}
